/**
 * Driver leg on the tracking screen (Promise Time Phase 2). Rendered from the order that
 * useOrder already polls: Order.delivery (mirrored by order-service from delivery.* events)
 * and Order.promise.current. Never computes an ETA; never subscribes to RTDB.
 */
import type { OrderOutput } from "@/api/online-ordering-service/model";
import { formatTime } from "@/lib/orderEta";

export type DriverLeg =
  | { kind: "to_store"; name: string; minutes: number }
  | { kind: "at_store"; name: string }
  | { kind: "arriving"; name: string; from: string; to: string }
  | { kind: "en_route"; name: string; minutes: number | null }
  | { kind: "at_customer"; name: string }
  | { kind: "assigned"; name: string };

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const DEPARTED = new Set(["picked_up", "in_transit"]);
const FINISHED = new Set(["delivered", "cancelled", "returned_to_store", "failed_delivery"]);

function firstName(full: string | null | undefined): string | null {
  const first = (full ?? "").trim().split(/\s+/)[0];
  return first ? first : null;
}

function positive(n: unknown): number | null {
  return typeof n === "number" && n > 0 ? n : null;
}

export function driverLegFor(
  order: OrderOutput | null | undefined,
  opts: { locale?: string; showPromise: boolean },
): DriverLeg | null {
  const delivery = order?.delivery;
  const name = firstName(delivery?.driverName);
  if (!delivery || !name) return null;
  // The mirror is lower-case; accept DMS upper-case enum values too.
  const status = (delivery.status ?? "").toLowerCase();
  if (FINISHED.has(status)) return null;
  if (status === "at_customer") return { kind: "at_customer", name };
  if (status === "at_store") return { kind: "at_store", name };

  const current = order?.promise?.current;
  const components = current?.components ?? {};

  if (DEPARTED.has(status)) {
    const window = current?.arriveAt;
    if (opts.showPromise && window?.earliest && window?.latest) {
      return {
        kind: "arriving",
        name,
        from: formatTime(new Date(window.earliest), opts.locale),
        to: formatTime(new Date(window.latest), opts.locale),
      };
    }
    const minutes = (opts.showPromise ? positive(components.travelMinutes) : null) ?? positive(delivery.etaToCustomerMinutes);
    return { kind: "en_route", name, minutes };
  }

  const toStore = (opts.showPromise ? positive(components.driverToStoreMinutes) : null) ?? positive(delivery.etaToStoreMinutes);
  return toStore ? { kind: "to_store", name, minutes: toStore } : { kind: "assigned", name };
}

export function driverLegText(leg: DriverLeg, t: TFn): string {
  switch (leg.kind) {
    case "to_store":
      return t("v2.tracking.driver_to_store", {
        name: leg.name,
        min: leg.minutes,
        defaultValue: `${leg.name} is ${leg.minutes} min from the store`,
      });
    case "at_store":
      return t("v2.tracking.driver_at_store", {
        name: leg.name,
        defaultValue: `${leg.name} is at the restaurant collecting your order`,
      });
    case "at_customer":
      return t("v2.tracking.driver_at_customer", {
        name: leg.name,
        defaultValue: `Your rider ${leg.name} has arrived`,
      });
    case "arriving":
      return t("v2.tracking.driver_arriving", {
        name: leg.name,
        from: leg.from,
        to: leg.to,
        defaultValue: `${leg.name} is on the way. Arrives ${leg.from} to ${leg.to}`,
      });
    case "en_route":
      return leg.minutes
        ? t("v2.tracking.driver_en_route_min", {
            name: leg.name,
            min: leg.minutes,
            defaultValue: `${leg.name} is on the way, about ${leg.minutes} min`,
          })
        : t("v2.tracking.driver_on_way", { name: leg.name, defaultValue: `${leg.name} is on the way` });
    case "assigned":
      return t("v2.tracking.driver_assigned", {
        name: leg.name,
        defaultValue: `${leg.name} will pick up your order`,
      });
  }
}
